/**
 * GEOLOP × TailAdmin BİLEŞEN KİTİ — şablonun kart/başlık/sekme/metrik kalıpları.
 * Sayfalar bunları kullanır; böylece her ekranda aynı boşluk, köşe, gölge, tipografi
 * ve light/dark davranışı olur. (Şablon: TailAdmin, MIT)
 */
import { useState, type ReactNode } from "react";
import { MoreHorizontal, ArrowUp, ArrowDown } from "lucide-react";

/** Sayfa başlığı: başlık + açıklama + sağda aksiyonlar */
export function GlPageHeader({ title, desc, actions, icon }: { title: string; desc?: string; actions?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        {icon && (
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400">
            {icon}
          </span>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white/90">{title}</h1>
          {desc && <p className="mt-1 text-theme-sm text-gray-500 dark:text-gray-400">{desc}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Bölüm kartı: başlık şeridi (opsiyonel açıklama + sağ aksiyon / üç nokta) + gövde */
export function GlSection({
  title, desc, action, menu, children, className = "", bodyClass = "",
}: {
  title?: string; desc?: string; action?: ReactNode; menu?: ReactNode;
  children: ReactNode; className?: string; bodyClass?: string;
}) {
  return (
    <div className={`rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] ${className}`}>
      {(title || action || menu) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-800 md:px-6">
          <div className="min-w-0">
            {title && <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">{title}</h3>}
            {desc && <p className="mt-0.5 text-theme-sm text-gray-500 dark:text-gray-400">{desc}</p>}
          </div>
          <div className="flex items-center gap-2">{action}{menu && <GlDots>{menu}</GlDots>}</div>
        </div>
      )}
      <div className={`px-5 py-5 md:px-6 ${bodyClass}`}>{children}</div>
    </div>
  );
}

/** Üç nokta menüsü (TailAdmin kart köşesi) */
export function GlDots({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-white/90">
        <MoreHorizontal className="h-5 w-5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-20 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-theme-lg dark:border-gray-800 dark:bg-gray-900">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

/** Metrik kartı: ikon + etiket + büyük değer + değişim rozeti */
export function GlMetric({
  label, value, icon, delta, hint,
}: { label: string; value: ReactNode; icon?: ReactNode; delta?: number | null; hint?: string }) {
  const up = (delta ?? 0) >= 0;
  return (
    <div className="gl-card">
      {icon && (
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-white/90">
          {icon}
        </div>
      )}
      <div className={`flex items-end justify-between ${icon ? "mt-4" : ""}`}>
        <div className="min-w-0">
          <span className="gl-label">{label}</span>
          <h4 className="gl-metric">{value}</h4>
          {hint && <p className="mt-1 text-theme-xs text-gray-400">{hint}</p>}
        </div>
        {delta != null && (
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-theme-xs font-medium ${
            up ? "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500" : "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500"
          }`}>
            {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {Math.abs(Math.round(delta))}%
          </span>
        )}
      </div>
    </div>
  );
}

/** Segment sekmeler (TailAdmin "Daily Sales | Online Sales | New Users") */
export function GlTabs<T extends string>({
  items, value, onChange, size = "md",
}: { items: { key: T; label: string }[]; value: T; onChange: (k: T) => void; size?: "sm" | "md" }) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => onChange(it.key)}
          className={`rounded-md font-medium transition ${size === "sm" ? "px-2.5 py-1 text-theme-xs" : "px-3 py-1.5 text-theme-sm"} ${
            value === it.key
              ? "bg-white text-gray-800 shadow-theme-xs dark:bg-white/10 dark:text-white/90"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white/90"
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/** Boş durum */
export function GlEmpty({ title, desc, action, icon }: { title: string; desc?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-25 px-6 py-12 text-center dark:border-gray-700 dark:bg-white/[0.02]">
      {icon && <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800">{icon}</div>}
      <p className="text-theme-sm font-medium text-gray-700 dark:text-gray-200">{title}</p>
      {desc && <p className="mt-1 max-w-md text-theme-xs text-gray-500 dark:text-gray-400">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Birincil / ikincil buton (TailAdmin ölçüleri) */
export function GlButton({
  children, onClick, variant = "primary", size = "md", disabled, title, className = "", type = "button",
}: {
  children: ReactNode; onClick?: () => void; variant?: "primary" | "outline" | "ghost" | "danger";
  size?: "sm" | "md"; disabled?: boolean; title?: string; className?: string; type?: "button" | "submit";
}) {
  const base = `inline-flex items-center justify-center gap-2 rounded-lg font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
    size === "sm" ? "px-3 py-2 text-theme-xs" : "px-4 py-2.5 text-theme-sm"
  }`;
  const styles = {
    primary: "bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/[0.03]",
    ghost: "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5",
    danger: "border border-error-200 bg-white text-error-600 hover:bg-error-50 dark:border-error-500/30 dark:bg-transparent dark:text-error-400 dark:hover:bg-error-500/10",
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${styles} ${className}`}>
      {children}
    </button>
  );
}

/** Etiket-değer satırı (detay listeleri) */
export function GlRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-2.5 last:border-0 dark:border-gray-800">
      <span className="text-theme-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-right text-theme-sm font-medium text-gray-800 dark:text-white/90">{children}</span>
    </div>
  );
}
